import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import { Paid, Refresh } from '@mui/icons-material'
import type { WarzoneCoinsExtrato, WarzoneCoinsHistoricoItem } from '../types'
import { apiService } from '../services/api'
import { WARZONE_COIN_IMAGE_URL } from '../properties'

const formatCoins = (value: number | null | undefined) => (Number(value ?? 0) || 0).toLocaleString('pt-BR')

const BRAZIL_TIMEZONE = 'America/Sao_Paulo'
const BACKEND_IMPLICIT_OFFSET = '-06:00'

const formatDateTime = (value: string | undefined) => {
  if (!value) return '—'
  const raw = String(value).trim()
  const hasExplicitTimezone = /([zZ]|[+\-]\d{2}:\d{2})$/.test(raw)
  const iso = hasExplicitTimezone ? raw : `${raw}${BACKEND_IMPLICIT_OFFSET}`
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString('pt-BR', { timeZone: BRAZIL_TIMEZONE })
}

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  const normalized = String(tipo).toUpperCase()
  const isEntrada = normalized === 'ENTRADA'
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.4,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 900,
        bgcolor: isEntrada ? 'rgba(0,230,118,0.14)' : 'rgba(255,109,0,0.14)',
        border: isEntrada ? '1px solid rgba(0,230,118,0.30)' : '1px solid rgba(255,109,0,0.30)',
        color: 'text.primary'
      }}
    >
      {normalized || '—'}
    </Box>
  )
}

const SummaryStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Card>
    <CardContent sx={{ p: 2.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
        <Box
          component="img"
          src={WARZONE_COIN_IMAGE_URL}
          alt="Warzone Coin"
          sx={{ width: 18, height: 18, opacity: 0.95 }}
          loading="lazy"
        />
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          {value}
        </Typography>
      </Box>
    </CardContent>
  </Card>
)

const WarzoneCoinsExtratoPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<WarzoneCoinsExtrato | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(50)

  const rows = useMemo<WarzoneCoinsHistoricoItem[]>(() => data?.historico?.content ?? [], [data?.historico?.content])
  const totalElements = Number(data?.historico?.totalElements ?? 0) || 0

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const extrato = await apiService.getWarzoneCoinsExtrato({ page, size: rowsPerPage })
      setData(extrato)
    } catch (e: any) {
      setError(e?.message || 'Erro ao consultar extrato de Warzone Coins.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let mounted = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const extrato = await apiService.getWarzoneCoinsExtrato({ page, size: rowsPerPage })
        if (!mounted) return
        setData(extrato)
      } catch (e: any) {
        if (!mounted) return
        setError(e?.message || 'Erro ao consultar extrato de Warzone Coins.')
      } finally {
        if (!mounted) return
        setLoading(false)
      }
    }
    void run()
    return () => {
      mounted = false
    }
  }, [page, rowsPerPage])

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Paid />
              Warzone Coins
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Saldo e extrato do usuário.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<Refresh />} onClick={load} disabled={loading}>
            Atualizar
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SummaryStat label="Saldo" value={formatCoins(data?.saldo)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <SummaryStat label="Total adicionado" value={formatCoins(data?.totalAdicionado)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <SummaryStat label="Total gasto" value={formatCoins(data?.totalGasto)} />
          </Grid>
        </Grid>

        <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 2.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Histórico
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Entradas e saídas de Warzone Coins.
            </Typography>
          </Box>

          {loading && !data ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 900 }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 900 }} align="right">
                        Valor Total
                      </TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Descrição</TableCell>
                      <TableCell sx={{ fontWeight: 900 }}>Data</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                          Nenhum lançamento encontrado.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, idx) => (
                        <TableRow key={`${row.createdAt}-${idx}`} hover>
                          <TableCell>
                            <TipoBadge tipo={row.tipo} />
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 900, whiteSpace: 'nowrap' }}>
                            {formatCoins(row.quantidade)}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 520 }}>{row.descricao || '—'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.createdAt)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalElements}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10))
                  setPage(0)
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Linhas"
              />
            </>
          )}
        </Paper>
      </Stack>
    </Container>
  )
}

export default WarzoneCoinsExtratoPage
